import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Lock, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) {
      setError(res.error || "Credenciales incorrectas");
      toast.error(res.error || "Credenciales incorrectas");
      return;
    }
    toast.success(`Bienvenido, ${res.user.name}`);
    if (res.user.must_change_password) {
      navigate("/change-password", { replace: true });
    } else {
      navigate(res.user.role === "admin" ? "/admin" : "/portal");
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-6 relative overflow-hidden">
      <div className="absolute inset-0 opacity-30">
        <img
          src="https://images.unsplash.com/photo-1515703944563-dbcfbf121b3d?auto=format&fit=crop&q=85&w=2000"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/50 via-[#0a0a0a]/85 to-[#0a0a0a]" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-8">
          <ArrowLeft className="w-4 h-4" /> Volver a la web
        </Link>

        <div className="bg-black/60 backdrop-blur-xl border border-white/10 rounded-2xl p-10 shadow-2xl">
          <div className="w-12 h-12 rounded-xl border border-[#D4AF37]/40 grid place-items-center mb-6">
            <Lock className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
          </div>
          <h1 className="font-serif text-3xl text-white mb-2">Área cliente</h1>
          <p className="text-zinc-400 mb-8 text-sm">
            Accede con tu email y contraseña facilitados por ANV Asesores.
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="text-xs uppercase tracking-[0.24em] text-zinc-500 mb-2 block">Email</label>
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="w-full bg-transparent border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none transition-colors"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.24em] text-zinc-500 mb-2 block">Contraseña</label>
              <div className="relative">
                <input
                  required
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                  className="w-full bg-transparent border border-white/10 rounded-lg px-4 py-3 pr-12 text-white focus:border-[#D4AF37] focus:outline-none transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  data-testid="toggle-password"
                  aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-[#D4AF37] transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#FDE047] transition-colors disabled:opacity-60"
            >
              {loading ? "Accediendo…" : "Acceder"}
            </button>
          </form>

          <div className="mt-6 flex items-center justify-between text-xs text-zinc-500">
            <Link to="/forgot-password" data-testid="forgot-link" className="hover:text-[#D4AF37]">
              ¿Olvidaste tu contraseña?
            </Link>
            <Link to="/contacto" className="hover:text-[#D4AF37]">
              ¿Sin cuenta? Contáctanos
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
